/**
 * Beacon API client singleton.
 *
 * Same-origin '/api' is proxied by Vite to the worker in dev,
 * served by Cloudflare Pages → Worker route in production.
 *
 * In dev (running on localhost), we send `X-Beacon-Dev-User` so the
 * worker's `requireAuth` middleware resolves a viewer without requiring
 * a real magic-link session. The worker only honours this header when
 * its own ENVIRONMENT=development. Change `DEV_USER_ID` here to test
 * different viewers.
 */

import { BeaconClient } from '@beacon/api-client';

/**
 * Default dev viewer when no per-tab override is set. Override with
 * `localStorage.setItem('beaconDevUserId', 'user-jacinta')` in the
 * console to switch personas — used by Playwright persona contexts
 * which set this in addInitScript before the app boots.
 *
 * Hosts:
 *   - localhost           → default to user-charlie (zero-friction dev)
 *   - *.workers.dev       → no default; set localStorage to opt in
 *                           (worker still gates the header to ENVIRONMENT
 *                           !== 'production', so leaking it from the
 *                           prod SPA is a no-op anyway)
 *   - prod custom domain  → no default; header ignored by worker
 */
const DEFAULT_DEV_USER_ID = 'user-charlie';

function isLocalDevHost(): boolean {
  if (typeof location === 'undefined') return false;
  return location.hostname === 'localhost';
}

function resolveDevUserId(): string | undefined {
  if (typeof localStorage !== 'undefined') {
    const override = localStorage.getItem('beaconDevUserId');
    if (override) return override;
  }
  if (isLocalDevHost()) return DEFAULT_DEV_USER_ID;
  return undefined;
}

/** Read the non-HttpOnly `beacon_csrf` cookie at request time. */
function readCsrfCookie(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const m = document.cookie.match(/(?:^|; )beacon_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]!) : undefined;
}

export const beaconClient = new BeaconClient({
  baseUrl: '',
  devUserId: resolveDevUserId,
  csrfToken: readCsrfCookie,
});
